import React, { useState, useMemo } from 'react';
import { Table, Button, Avatar } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import AddProviderModal from 'components/AddProviderModal';
import type { IPracticeByProviderRow } from 'types/marketing';
import { centsToYuan, isProviderBudgetExhausted } from 'utils/marketing';
import BudgetExhaustedTag from 'pages/dashboard/Marketing/components/BudgetExhaustedTag';
import s from './s.module.less';

type Props = {
    providers: IPracticeByProviderRow[];
    hasProListing: boolean;
    stdListingCount: number;
    onSetupClick?: () => void;
};

const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return (parts[0]?.[0] ?? '').toUpperCase();
};

const ByProviderTable: React.FC<Props> = ({ providers, hasProListing, stdListingCount, onSetupClick }) => {
    const [addProviderOpen, setAddProviderOpen] = useState(false);
    const showAddCta = stdListingCount < 5;

    const columns = useMemo<ColumnsType<IPracticeByProviderRow>>(() => {
        const cols: ColumnsType<IPracticeByProviderRow> = [
            {
                title: 'Provider',
                dataIndex: 'name',
                key: 'name',
                width: 240,
                sorter: (a, b) => a.name.localeCompare(b.name),
                render: (_, record) => (
                    <div className={s.providerCell}>
                        {record.avatar ? (
                            <Avatar src={record.avatar} size={36} />
                        ) : (
                            <Avatar size={36} className={s.avatar}>
                                {getInitials(record.name)}
                            </Avatar>
                        )}
                        <div className={s.providerInfo}>
                            <span className={s.providerName}>{record.name}</span>
                            <span className={s.providerCred}>
                                {record.services?.map((svc) => svc.specialtyName).join(', ')}
                            </span>
                        </div>
                        {isProviderBudgetExhausted(record) && (
                            <BudgetExhaustedTag
                                className={s.providerTag}
                                pausedSince={record.budgetExhaustedPausedAt}
                            />
                        )}
                    </div>
                ),
            },
            {
                title: 'Bookings',
                dataIndex: 'bookings',
                key: 'bookings',
                align: 'right',
                defaultSortOrder: 'descend',
                sorter: (a, b) => (a.bookings || 0) - (b.bookings || 0),
                render: (val) => <strong>{val || 0}</strong>,
            },
            {
                title: 'Impressions',
                dataIndex: 'impressions',
                key: 'impressions',
                align: 'right',
                sorter: (a, b) => (a.impressions || 0) - (b.impressions || 0),
                render: (val) => (val || 0).toLocaleString(),
            },
            {
                title: 'Impression share',
                dataIndex: 'impressionShare',
                key: 'impressionShare',
                align: 'right',
                sorter: (a, b) => (a.impressionShare || 0) - (b.impressionShare || 0),
                render: (val) => `${Math.round(val || 0)}%`,
            },
            {
                title: hasProListing ? 'Bid' : '',
                dataIndex: 'bidAmountCents',
                key: 'bid',
                align: 'right',
                render: (val, record) => {
                    if (hasProListing && record.onProListing) {
                        return `$${centsToYuan(val)}`;
                    }
                    return (
                        <Button
                            size="small"
                            icon={<PlusOutlined />}
                            className={s.addToProBtn}
                            onClick={(e) => {
                                e.stopPropagation();
                                onSetupClick?.();
                            }}
                        >
                            Add to Pro-Listing
                        </Button>
                    );
                },
            },
        ];
        return cols;
    }, [hasProListing, onSetupClick]);

    return (
        <div className={s.wrap}>
            <Table<IPracticeByProviderRow>
                columns={columns}
                dataSource={providers}
                rowKey="providerId"
                pagination={false}
                className={s.provTable}
                locale={{ emptyText: 'No providers match the current filters.' }}
            />
            {showAddCta && (
                <div className={s.addProviderCta}>
                    <div className={s.ctaTitle}>
                        Add more providers to grow your group practice and unlock premium features
                    </div>
                    <div className={s.ctaSub}>Need &ge;5 providers for Group Practice benefits</div>
                    <Button type="primary" className={s.ctaBtn} onClick={() => setAddProviderOpen(true)}>
                        Add provider
                    </Button>
                </div>
            )}
            <AddProviderModal open={addProviderOpen} onClose={() => setAddProviderOpen(false)} />
        </div>
    );
};

export default ByProviderTable;
